import { Engine, WebGPUEngine } from '@babylonjs/core';

export interface EngineHandle {
  /** Either an Engine (WebGL2) or WebGPUEngine. Same render-loop API. */
  engine: Engine | WebGPUEngine;
  /** "webgpu" | "webgl2" */
  backend: 'webgpu' | 'webgl2';
}

export async function createEngine(canvas: HTMLCanvasElement): Promise<EngineHandle> {
  // Default to WebGL2. Babylon's WebGPU backend has known gaps that affect this app:
  // MotionBlurPostProcess emits invalid WGSL, and adding a custom PostProcess on top of
  // an MSAA-enabled DefaultRenderingPipeline produces invalid pipeline state (the radial
  // speed-blur blackens the screen mid-drive). The project doesn't use any WebGPU-only
  // feature, so WebGL2 is purely additive. Opt-in to WebGPU via ?webgpu when testing
  // future Babylon improvements; ?webgl2 stays as an explicit no-op for symmetry.
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const forceWebGl2 = params?.has('webgl2') ?? false;
  const forceWebGpu = params?.has('webgpu') ?? false;

  if (!forceWebGl2 && forceWebGpu) {
    try {
      const engine = new WebGPUEngine(canvas, {
        antialias: true,
        stencil: true,
      });
      await engine.initAsync();
      return { engine, backend: 'webgpu' };
    } catch (err) {
      console.warn('WebGPU init failed, falling back to WebGL2', err);
    }
  }
  const engine = new Engine(canvas, true, {
    preserveDrawingBuffer: false,
    stencil: true,
    powerPreference: 'high-performance',
  });
  return { engine, backend: 'webgl2' };
}
